<script setup lang="ts">
import InlineSvg from 'vue-inline-svg';
import { Field } from 'vee-validate';

import UploadIcon from '@clovyr/bed/assets/images/uploadIcon.svg';
import Tooltip from '@clovyr/bed/components/blocks/Tooltip.vue';
import { FileAPI } from '@clovyr/pollen/file';

import ClovyrEditor from '@/components/forms/ClovyrEditor.vue';
import FieldInfoIcon from '@/components/icons/FieldInfoIcon.vue';
import { useDrag } from '@/composables/useDrag';

defineOptions({
  inheritAttrs: false,
});

const emit = defineEmits(['update:modelValue']);
const model = defineModel<string>();

const { allowDrop, tooltip } = defineProps<{
  name: string;
  label: string;
  errorMessage?: string;
  tooltip?: string;
  allowDrop?: boolean;
}>();

const uploadInput = ref<HTMLInputElement | null>(null);

async function onFileChange(event: Event) {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) {
    const f = await new FileAPI().upload(file);
    emit('update:modelValue', f.fileurl);
  }
}

async function onDropFile(file: File) {
  if (file.type.indexOf('image') >= 0) {
    const f = await new FileAPI().upload(file);
    emit('update:modelValue', f.fileurl);
  }
}

async function onClickUpload() {
  if (uploadInput.value) {
    uploadInput.value.showPicker();
  }
}

const isDropEnabled = computed(() => allowDrop);
const { isDragging, onDragOver, onDragEnter, onDragLeave, onDrop } = useDrag(
  isDropEnabled,
  onDropFile,
);
</script>

<template>
  <Field type="text" :name="name" :model-value="model">
    <div
      class="textarea-block"
      :class="{
        'textarea-block--has-error': !!errorMessage,
        upload: allowDrop && isDragging,
        'has-icon': allowDrop,
      }"
    >
      <div class="textarea-block__label-holder">
        <label :for="name" class="textarea-block__label">{{ label }}</label>
        <div class="controls">
          <div v-if="allowDrop" class="image-holder" @click.stop.prevent="onClickUpload">
            <input
              type="file"
              accept="image/*"
              class="file-upload"
              @change="onFileChange"
              ref="uploadInput"
            />
            <inline-svg :src="UploadIcon" class="icon upload-icon" />
          </div>
          <Tooltip v-if="tooltip" :content="tooltip">
            <FieldInfoIcon />
          </Tooltip>
        </div>
      </div>

      <div
        class="textarea-block__input-holder"
        @dragover="onDragOver"
        @dragenter="onDragEnter"
        @dragleave="onDragLeave"
        @drop="onDrop"
      >
        <ClovyrEditor
          class="textarea-block__input"
          language="shell"
          v-bind="$attrs"
          v-model="model"
          :allow-drop="allowDrop"
        />
      </div>

      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
    </div>
  </Field>
</template>

<style scoped lang="scss">
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
.textarea-block__label-holder {
  display: flex;
  gap: 5px;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 3px;
}

.controls {
  display: flex;
  flex-direction: row;
  gap: 5px;
}

.upload {
  label {
    color: #d375ff;
  }
  svg.upload-icon {
    stroke: #d375ff;
    stroke-width: 0.6px;
  }
}

input.file-upload {
  display: none;
}
</style>
